import type { OrderStatusResponse } from '@/utils/DatabaseOperations'
import { AlertBanner } from '@/components/common/AlertBanner'
import { Badge } from '@helix-ds/react'
import { ChevronDown, ChevronSort, ChevronUp, Search } from 'flowbite-react-icons/outline'
import { motion } from 'framer-motion'
import { useMemo, useState } from 'react'
import {
  toWorkspaceOrderGroups,
  type UseWorkspaceFiltersResult,
  type WorkspaceSort,
  type WorkspaceSortColumn,
} from './useWorkspaceFilters'
import { WorkspaceOrderCard } from './WorkspaceOrderCard'
import { WorkspaceStatusFilter } from './WorkspaceStatusFilter'
import {
  calculateAge,
  comparatorRelationshipLabel,
  formatMrnDisplay,
  formatShortDate,
  getWorkspaceDraftActionHint,
  getWorkspaceProviderDisplay,
  getWorkspaceStatusBadgeClass,
  getWorkspaceStatusDisplay,
  isPrimaryGuardianComparatorRow,
  workspaceOrderNumberDisplay,
} from './workspaceOrderPresentation'

export type WorkspaceOrdersDataTableProps = {
  /** The current server page (orders + drafts), already searched, filtered and sorted. */
  orders: OrderStatusResponse[]
  /** Filter/sort/page state owned by the page so it survives refetches. */
  filters: UseWorkspaceFiltersResult
  ordersLoading: boolean
  ordersError: string | null
  permissions: string[]
  roles: string[]
  onOrderClick: (order: OrderStatusResponse) => void | Promise<void>
}

/**
 * Sortable desktop `<th>`. Uses a real `<button>` inside the `<th>` for native keyboard/focus
 * semantics and sets `aria-sort` on the `<th>`. aria-label interpolates only the column label +
 * fixed strings (no PHI, §9a).
 */
function SortableHeader({
  column,
  label,
  sort,
  onToggle,
}: {
  column: WorkspaceSortColumn
  label: string
  sort: WorkspaceSort | null
  onToggle: (c: WorkspaceSortColumn) => void
}) {
  const active = sort?.column === column
  const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
  const Icon = active ? (sort.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronSort
  return (
    <th scope="col" aria-sort={ariaSort} className="h-11 px-5 py-0 text-left align-middle">
      <button
        type="button"
        onClick={() => onToggle(column)}
        className="inline-flex items-center gap-1.5 rounded-full px-1 text-sm font-medium text-text-body hover:text-text-heading focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:text-gray-300 dark:hover:text-white"
        aria-label={`Sort by ${label}${active ? `, currently ${ariaSort}` : ''}`}
      >
        <span>{label}</span>
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
      </button>
    </th>
  )
}

/**
 * Workspace orders grid rendered fully in React. Comparator rows are presentation-only
 * children of their proband order and never participate in pagination totals.
 */
export function WorkspaceOrdersDataTable({
  orders,
  filters,
  ordersLoading,
  ordersError,
  permissions,
  roles,
  onOrderClick,
}: WorkspaceOrdersDataTableProps) {
  /** Per-order: true = comparator sub-rows visible. Default collapsed when key unset. */
  const [comparatorGroupsExpanded, setComparatorGroupsExpanded] = useState<Record<string, boolean>>({})
  const {
    search,
    setSearch,
    sort,
    toggleSort,
    page,
    setPage,
    totalGroups,
    pageCount,
    rangeStart,
    rangeEnd,
    statusFilter,
    toggleStatus,
    clearStatusFilter,
    selectAllStatuses,
    deselectAllStatuses,
    statusOptions,
    hasActiveFilters,
    allStatusesSelected,
    ghostStatusCount,
  } = filters
  const pagedGroups = useMemo(() => toWorkspaceOrderGroups(orders), [orders])

  const toggleComparators = (groupKey: string) => {
    setComparatorGroupsExpanded((prev) => ({
      ...prev,
      [groupKey]: !(prev[groupKey] ?? false),
    }))
  }

  const noOrdersAtAll = totalGroups === 0 && !hasActiveFilters

  return (
    <div className="workspace-orders-datatable overflow-hidden rounded-base border border-border-base bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-col gap-3 border-b border-border-base px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center dark:border-gray-700">
        <label className="relative block w-full max-w-sm">
          <span className="sr-only">Search orders</span>
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-text-body-subtle dark:text-gray-500">
            <Search className="h-4 w-4" aria-hidden />
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search for patients or orders"
            className="w-full rounded-full border border-border-base bg-white py-2 pl-9 pr-3 text-sm text-text-heading placeholder:text-text-body-subtle focus:border-border-brand focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
          />
        </label>
        <WorkspaceStatusFilter
          options={statusOptions}
          selected={statusFilter}
          allSelected={allStatusesSelected}
          onToggle={toggleStatus}
          onClear={clearStatusFilter}
          onSelectAll={selectAllStatuses}
          onDeselectAll={deselectAllStatuses}
        />
      </div>
      {ghostStatusCount > 0 && (
        <div className="border-b border-border-base px-4 py-2 dark:border-gray-700">
          {/* Local shared component: src/components/common/AlertBanner.tsx. API is `variant` ('info' |
              'error') and `onClose`, NOT `color`/`onDismiss`. Omitting onClose keeps it persistent. */}
          <AlertBanner variant="info">
            {ghostStatusCount} selected filter{ghostStatusCount > 1 ? 's have' : ' has'} no matching orders right now.
          </AlertBanner>
        </div>
      )}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-left text-sm text-text-body dark:text-gray-300">
          <thead>
            <tr className="border-y border-border-base bg-background-secondary-medium text-sm font-medium capitalize text-text-body dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <th className="w-1 p-0" scope="col" aria-hidden />
              <th className="h-11 px-5 py-0 text-left align-middle" scope="col">
                Case #
              </th>
              <th className="h-11 px-5 py-0 text-left align-middle" scope="col">
                Patient • MRN
              </th>
              <th className="h-11 px-5 py-0 text-left align-middle" scope="col">
                DOB • Age • Sex
              </th>
              <th className="h-11 px-5 py-0 text-left align-middle" scope="col">
                Provider
              </th>
              <SortableHeader column="orderDate" label="Order Submitted" sort={sort} onToggle={toggleSort} />
              <SortableHeader column="lastUpdate" label="Last Updated" sort={sort} onToggle={toggleSort} />
              <th className="h-11 px-5 py-0 text-left align-middle" scope="col">
                Comparators
              </th>
              <th
                className="h-11 px-5 py-0 text-left align-middle"
                scope="col"
                aria-sort={sort?.column === 'status' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button
                  type="button"
                  onClick={() => toggleSort('status')}
                  className="inline-flex items-center gap-1.5 rounded-full px-1 text-sm font-medium text-text-body hover:text-text-heading focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:text-gray-300 dark:hover:text-white"
                  aria-label={`Sort by Status${
                    sort?.column === 'status' ? `, currently ${sort.dir === 'asc' ? 'ascending' : 'descending'}` : ''
                  }`}
                >
                  <span>Status • Action</span>
                  {sort?.column === 'status' ? (
                    sort.dir === 'asc' ? (
                      <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                    ) : (
                      <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                    )
                  ) : (
                    <ChevronSort className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {ordersLoading ? (
              <tr>
                <td colSpan={9} className="px-5 py-8 text-center text-sm text-text-body-subtle dark:text-gray-400">
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-brand-600 border-r-transparent"
                      role="status"
                    />
                    <span>Loading orders…</span>
                  </div>
                </td>
              </tr>
            ) : ordersError ? (
              <tr>
                <td colSpan={9} className="px-5 py-8 text-center text-sm text-error-600 dark:text-red-400">
                  Error loading orders: {ordersError}
                </td>
              </tr>
            ) : noOrdersAtAll ? (
              <tr>
                <td colSpan={9} className="px-5 py-8 text-center text-sm text-text-body-subtle dark:text-gray-400">
                  {!permissions.includes('order:view') ? (
                    <>You do not have permission to view orders. Please contact your administrator.</>
                  ) : roles.includes('org_admin') ? (
                    <>No orders have been created for your organization yet.</>
                  ) : (
                    <>You have not created any orders yet.</>
                  )}
                </td>
              </tr>
            ) : totalGroups === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-8 text-center text-sm text-text-body-subtle dark:text-gray-400">
                  No orders match the current filters.
                </td>
              </tr>
            ) : (
              pagedGroups.flatMap(({ order, comparators, groupKey }, groupIndex) => {
                const lastUpdate = formatShortDate(order.updated_at)
                const orderDateDisplay = formatShortDate(order.created_at)
                const { label: statusLabel } = getWorkspaceStatusDisplay(order.status)
                const statusBadgeClass = getWorkspaceStatusBadgeClass(order.status)
                const actionHint = getWorkspaceDraftActionHint(order)
                const { main: providerMain, submittedBy: providerSubmittedBy } = getWorkspaceProviderDisplay(order)
                // Key expand-state / regionId / React keys on the unique groupKey (not raw order_id)
                // so two orders with an empty order_id don't collide (F6). Equals order_id normally.
                const comparatorsExpanded = comparatorGroupsExpanded[groupKey] ?? false
                const regionId = `workspace-order-${groupKey}-comparators`
                const orderNumberLabel = workspaceOrderNumberDisplay(order)
                // Zebra striping per Figma: alternate white / subtle-gray by group.
                const rowBgClass =
                  groupIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-background-secondary-medium dark:bg-gray-800/40'
                const orderNumberTitle =
                  order.status === 'draft'
                    ? `Draft — ordering session: ${order.order_id}`
                    : orderNumberLabel !== order.order_id
                      ? `Inflection ID: ${orderNumberLabel} (order UUID: ${order.order_id})`
                      : `Order ID: ${order.order_id}`

                const mainRow = (
                  <motion.tr
                    key={`${groupKey}-main`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    onClick={() => void onOrderClick(order)}
                    className={`cursor-pointer border-b border-border-base hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800/50 ${rowBgClass}`}
                  >
                    <td className="w-1 p-0" aria-hidden />
                    <td className="h-14 px-5 py-2 align-middle">
                      <span
                        title={orderNumberTitle}
                        className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline dark:text-purple-400 dark:hover:text-purple-300"
                      >
                        {orderNumberLabel}
                      </span>
                    </td>
                    <td className="h-14 px-5 py-2 align-middle">
                      <div className="text-base font-medium text-text-heading dark:text-white">
                        {order.patient_first_name || order.patient_last_name
                          ? `${order.patient_first_name ?? ''} ${order.patient_last_name ?? ''}`.trim()
                          : 'Patient Data N/A'}
                      </div>
                      <div className="text-xs text-text-body-subtle dark:text-gray-400">
                        {order.patient_mrn
                          ? formatMrnDisplay(order.patient_mrn)
                          : order.external_case_key
                            ? `Case ${order.external_case_key}`
                            : 'No case key'}
                      </div>
                    </td>
                    <td className="h-14 px-5 py-2 align-middle">
                      <div className="text-sm text-text-heading dark:text-white">
                        {order.patient_date_of_birth ?? 'N/A'}
                      </div>
                      <div className="text-xs text-text-body-subtle dark:text-gray-400">
                        {calculateAge(order.patient_date_of_birth ?? undefined)} • {order.patient_sex ?? 'U'}
                      </div>
                    </td>
                    <td className="h-14 px-5 py-2 align-middle">
                      <div className="text-sm text-text-heading dark:text-white">{providerMain}</div>
                      {providerSubmittedBy && (
                        <div className="text-xs text-text-body-subtle dark:text-gray-400">{providerSubmittedBy}</div>
                      )}
                    </td>
                    <td className="h-14 px-5 py-2 align-middle text-sm text-text-heading dark:text-white">
                      {orderDateDisplay}
                    </td>
                    <td className="h-14 px-5 py-2 align-middle text-sm text-text-heading dark:text-white">
                      {lastUpdate}
                    </td>
                    <td className="h-14 px-5 py-2 align-middle text-sm">
                      {comparators.length > 0 ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleComparators(groupKey)
                          }}
                          className="inline-flex items-center gap-1.5 rounded-full border border-border-base bg-background-secondary-medium px-2.5 py-1 text-xs font-medium text-text-body hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-600/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          aria-expanded={comparatorsExpanded}
                          aria-controls={regionId}
                          aria-label={
                            comparatorsExpanded
                              ? `Hide comparators for order ${order.order_id}`
                              : `Show comparators for order ${order.order_id}`
                          }
                        >
                          <span>
                            {comparators.length} {comparators.length === 1 ? 'Relation' : 'Relations'}
                          </span>
                          <span
                            className={`inline-block transition-transform duration-150 ease-out ${
                              comparatorsExpanded ? '' : '-rotate-90'
                            }`}
                          >
                            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                          </span>
                        </button>
                      ) : (
                        <span className="text-text-heading dark:text-white">None</span>
                      )}
                    </td>
                    <td className="h-14 px-5 py-2 align-middle">
                      <div className="flex flex-col items-start justify-center gap-1.5">
                        <Badge className={statusBadgeClass}>{statusLabel}</Badge>
                        {actionHint && (
                          <span className="pl-2 text-xs font-normal leading-3 text-text-body dark:text-gray-400">
                            {actionHint}
                          </span>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                )

                if (comparators.length === 0) return [mainRow]
                if (!comparatorsExpanded) return [mainRow]

                const subRows = comparators.map((c, idx) => {
                  const isPg = isPrimaryGuardianComparatorRow(c)
                  const relLabel = comparatorRelationshipLabel(c.relationship)
                  const comparatorMrn = c.mrn
                  return (
                    <tr
                      key={`${groupKey}-cmp-${c.patient_id}`}
                      id={idx === 0 ? regionId : undefined}
                      className="border-b border-border-base bg-background-secondary-medium/80 dark:border-gray-700 dark:bg-gray-800/40"
                    >
                      <td className="w-1 p-0" aria-hidden />
                      <td className="h-14 px-5 py-2 align-middle" />
                      <td className="h-14 px-5 py-2 align-middle">
                        <div className="text-sm font-medium text-text-heading dark:text-white">
                          {c.first_name || c.last_name
                            ? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
                            : 'Name N/A'}
                        </div>
                        <div className="text-xs text-text-body-subtle dark:text-gray-400">
                          {formatMrnDisplay(comparatorMrn)}
                        </div>
                      </td>
                      <td className="h-14 px-5 py-2 align-middle">
                        <div className="text-sm text-text-heading dark:text-white">{c.date_of_birth ?? 'N/A'}</div>
                        <div className="text-xs text-text-body-subtle dark:text-gray-400">
                          {calculateAge(c.date_of_birth ?? undefined)} • {c.sex ?? 'U'}
                        </div>
                      </td>
                      <td className="h-14 px-5 py-2 align-middle">
                        <div className="text-sm text-text-heading dark:text-white">{providerMain}</div>
                        {providerSubmittedBy && (
                          <div className="text-xs text-text-body-subtle dark:text-gray-400">{providerSubmittedBy}</div>
                        )}
                      </td>
                      <td className="h-14 px-5 py-2 align-middle text-sm text-text-heading dark:text-white">
                        {orderDateDisplay}
                      </td>
                      <td className="h-14 px-5 py-2 align-middle text-sm text-text-heading dark:text-white">
                        {lastUpdate}
                      </td>
                      <td className="h-14 px-5 py-2 align-middle text-sm text-text-heading dark:text-white">
                        <span className="inline-flex items-center gap-1.5">
                          {isPg && <Badge color="gray">Guardian</Badge>}
                          {relLabel && <span>{relLabel}</span>}
                        </span>
                      </td>
                      <td className="h-14 px-5 py-2 align-middle">
                        <div className="flex flex-col items-start justify-center gap-1.5">
                          <Badge className={statusBadgeClass}>{statusLabel}</Badge>
                        </div>
                      </td>
                    </tr>
                  )
                })

                return [mainRow, ...subRows]
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 p-4 md:hidden">
        {ordersLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-text-body-subtle dark:text-gray-400">
            <div
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-brand-600 border-r-transparent"
              role="status"
            />
            <span>Loading orders…</span>
          </div>
        ) : ordersError ? (
          <div className="py-8 text-center text-sm text-error-600 dark:text-red-400">
            Error loading orders: {ordersError}
          </div>
        ) : noOrdersAtAll ? (
          <div className="py-8 text-center text-sm text-text-body-subtle dark:text-gray-400">
            {!permissions.includes('order:view') ? (
              <>You do not have permission to view orders. Please contact your administrator.</>
            ) : roles.includes('org_admin') ? (
              <>No orders have been created for your organization yet.</>
            ) : (
              <>You have not created any orders yet.</>
            )}
          </div>
        ) : totalGroups === 0 ? (
          <div className="py-8 text-center text-sm text-text-body-subtle dark:text-gray-400">
            No orders match the current filters.
          </div>
        ) : (
          pagedGroups.map((group) => (
            <WorkspaceOrderCard
              key={`${group.groupKey}-card`}
              group={group}
              expanded={comparatorGroupsExpanded[group.groupKey] ?? false}
              onToggleComparators={toggleComparators}
              onOrderClick={onOrderClick}
            />
          ))
        )}
      </div>

      {!ordersLoading && !ordersError && totalGroups > 0 && (
        <div className="flex flex-col gap-3 border-t border-border-base px-4 py-3 text-sm text-text-body sm:flex-row sm:items-center sm:justify-between dark:border-gray-700 dark:text-gray-300">
          <span>
            Showing {rangeStart} to {rangeEnd} of {totalGroups} orders
          </span>
          <nav className="flex items-center gap-2" aria-label="Orders pagination">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-full border border-border-base px-3 py-1 text-sm font-medium text-text-body hover:bg-background-secondary-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Previous
            </button>
            <span className="text-xs text-text-body-subtle dark:text-gray-400">
              Page {page} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              className="rounded-full border border-border-base px-3 py-1 text-sm font-medium text-text-body hover:bg-background-secondary-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  )
}
